import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import { locationsErrorMessageForDeadline } from "@/Shared/Locations/Validators/locationsErrorMessageForDeadline";
import { bookShipmentShouldShowDeadlineSection } from "./bookShipmentShouldShowDeadlineSection";

/**
 * The address screens' deadline rule, re-run at Review.
 *
 * The rule normally runs only on the address screen, and a customer who sets a
 * deadline there and then moves the pickup date on the quotes screen never sees
 * that screen again. Review would hand the server a deadline it refuses
 * (ENG-6859), and the customer would land on "nearly there" with no reason.
 * Same helper as the address screen, so the two can't disagree. The side is
 * named because this message shows far from the field it is about.
 */
export function bookShipmentReviewDeadlineError(
  shipment: PreBookingShipment,
): string | undefined {
  return (
    sideError(
      "Pickup",
      shipment.pickupLocation,
      LocationContext.Pickup,
      shipment.pickupDate,
      shipment.pickupDeadline,
      undefined,
    ) ??
    sideError(
      "Delivery",
      shipment.deliveryLocation,
      LocationContext.Delivery,
      shipment.pickupDate,
      shipment.deliveryDeadline,
      // The carrier's earliest delivery date only bounds the delivery deadline,
      // as on the address screen.
      shipment.selectedQuote?.expectedDeliveryDate,
    )
  );
}

function sideError(
  side: "Pickup" | "Delivery",
  location: PreQuoteLocation,
  context: LocationContext,
  pickupDate: string,
  deadline: string | undefined,
  earliestDeliveryDate: string | undefined,
): string | undefined {
  if (
    deadline === undefined ||
    !bookShipmentShouldShowDeadlineSection(location, context)
  ) {
    return undefined;
  }
  const message = locationsErrorMessageForDeadline(
    pickupDate,
    true,
    deadline,
    earliestDeliveryDate,
  );
  if (typeof message !== "string") return undefined;
  // "Deadline must be after pickup date" reads fine beside the field; standing
  // alone on Review it needs to say which one.
  return message.replace(/^Deadline/, `${side} deadline`);
}
